import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { CONDITION_CATEGORIES } from "../data/conditions";

export default function Conditions() {
  const [activeId, setActiveId] = useState(CONDITION_CATEGORIES[0].id);
  const active =
    CONDITION_CATEGORIES.find((c) => c.id === activeId) ||
    CONDITION_CATEGORIES[0];

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="conditions"
      data-testid="conditions-section"
      className="lfc-section relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#251047]/40 via-transparent to-[#251047]/40 pointer-events-none" />
      <div className="absolute top-1/4 right-0 w-[460px] h-[460px] rounded-full bg-[#2BB8B4]/10 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-1/4 -left-24 w-[420px] h-[420px] rounded-full bg-[#7B46D9]/15 blur-[120px] pointer-events-none" />

      <div className="lfc-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="lfc-eyebrow mb-4">Conditions we treat</div>
            <h2 className="lfc-h2">
              Skin, nails & lower-limb <br className="hidden md:block" />
              conditions in our care.
            </h2>
            <div className="lfc-divider-leaf mt-6" />
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-[#C2B4E6] leading-relaxed">
              From everyday concerns like corns and cracked heels to specialist treatment for
              fungal nails, ingrown toenails and diabetic foot care — every treatment is delivered
              in the comfort of your home.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div
          data-testid="conditions-tabs"
          className="mt-12 flex flex-wrap gap-2.5"
        >
          {CONDITION_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = cat.id === activeId;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveId(cat.id)}
                data-testid={`conditions-tab-${cat.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13.5px] font-medium border transition-all duration-300 ${
                  isActive
                    ? "bg-[#2BB8B4] text-[#0E0524] border-[#2BB8B4] shadow-[0_8px_22px_-10px_rgba(43,184,180,0.7)]"
                    : "bg-[#2D1456]/60 text-[#C2B4E6] border-[#4A2D7A] hover:border-[#2BB8B4]/60 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.7} />
                {cat.title}
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Image + intro */}
          <div className="lg:col-span-5">
            <div className="relative rounded-[1.75rem] overflow-hidden border border-[#4A2D7A] bg-[#2D1456]">
              <img
                key={active.id}
                src={active.image}
                alt={active.title}
                className="w-full h-[360px] lg:h-[460px] object-cover lfc-fade-up"
                data-testid={`conditions-image-${active.id}`}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1B0B3A]/70 via-transparent to-[#2BB8B4]/15 pointer-events-none" />

              <div className="absolute bottom-5 left-5 right-5 bg-[#1B0B3A]/85 backdrop-blur-md rounded-2xl border border-[#4A2D7A] p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2BB8B4] grid place-items-center text-[#0E0524] shrink-0">
                    <active.icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="leading-tight">
                    <div
                      className="text-[15px] font-semibold text-white"
                      style={{ fontFamily: "Outfit" }}
                    >
                      {active.title}
                    </div>
                    <div className="text-[12.5px] text-[#C2B4E6]">
                      Treated in the comfort of your home
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-5 text-[15.5px] text-[#C2B4E6] leading-relaxed">
              {active.blurb}
            </p>

            <button
              onClick={() => goTo("contact")}
              data-testid={`conditions-enquire-${active.id}`}
              className="mt-6 lfc-btn-secondary"
            >
              Enquire about {active.title.toLowerCase()}
              <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </button>
          </div>

          {/* Conditions grid */}
          <div className="lg:col-span-7">
            <div
              key={active.id}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 lfc-fade-up"
              data-testid={`conditions-list-${active.id}`}
            >
              {active.items.map((item) => (
                <div
                  key={item.name}
                  data-testid={`condition-${active.id}-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="lfc-card p-5 group"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full bg-[#2BB8B4] shrink-0 group-hover:scale-125 transition-transform" />
                    <div>
                      <div
                        className="text-[16px] font-semibold text-white leading-tight"
                        style={{ fontFamily: "Outfit" }}
                      >
                        {item.name}
                      </div>
                      <p className="mt-1.5 text-[13.5px] text-[#C2B4E6] leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
